/**
 * KYCComplianceReportPage — `/reports/kyc-compliance`.
 *
 * Wired to GET /v1/reports/kyc-compliance?period= on service-reporting-api.
 * Note: KYC tier_upgrade_request data is not yet in SourceReaders — this
 * report returns stub data until loadKycRequests() is wired.
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';

import { getKYCComplianceReport } from '../../lib/api/pre-built-reports';
import { ApiError } from '../../lib/api/client';
import { findPreBuiltReport } from '../../lib/api/reports';
import { DeferredNotice } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';
import { ReportShell } from './ReportShell';

export function KYCComplianceReportPage(): JSX.Element {
  const report = findPreBuiltReport('kyc-compliance');
  const [period, setPeriod] = useState<string>('THIS_MONTH');

  const query = useQuery({
    queryKey: ['pre-built', 'kyc-compliance', period],
    queryFn: () => getKYCComplianceReport(period),
    retry: false,
  });

  if (!report) return <p>Unknown report.</p>;

  const data = query.data;
  const denied =
    query.isError && query.error instanceof ApiError &&
    (query.error.status === 401 || query.error.status === 403);

  return (
    <ReportShell report={report}>
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Period</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period"
              className="px-3 py-2 text-sm border border-border rounded-lg bg-white">
              <option value="THIS_MONTH">This month</option>
              <option value="LAST_MONTH">Last month</option>
              <option value="THIS_YEAR">This year</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Total requests" value={data ? data.totalRequests.toLocaleString('en-US') : '—'} hint="KYC" icon={ShieldCheck} />
        <KPICard title="Approved" value={data ? data.approvedCount.toLocaleString('en-US') : '—'} hint="KYC" tone="success" />
        <KPICard title="Rejected" value={data ? data.rejectedCount.toLocaleString('en-US') : '—'} hint="KYC" tone="danger" />
        <KPICard title="Pending" value={data ? data.pendingCount.toLocaleString('en-US') : '—'} hint="KYC" tone="warning" />
      </div>

      {query.isPending && <p className="text-sm text-muted">Loading report…</p>}
      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reports/readKycCompliance</code> required.
        </DeferredNotice>
      )}
      {query.isError && !denied && (
        <DeferredNotice title="Report unavailable">{query.error.message}</DeferredNotice>
      )}

      {data && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Tier breakdown</h3>
            {data.stub && (
              <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                Preview data — full data coming soon
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Tier</th>
                  <th className="text-right px-4 py-2">Submitted</th>
                  <th className="text-right px-4 py-2">Approved</th>
                  <th className="text-right px-4 py-2">Rejected</th>
                  <th className="text-right px-4 py-2">Pending</th>
                  <th className="text-right px-4 py-2">Approval %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.rows.map((row) => (
                  <tr key={row.tier}>
                    <td className="px-4 py-2 text-xs">{row.tier}</td>
                    <td className="px-4 py-2 text-right text-xs">{row.submitted.toLocaleString('en-US')}</td>
                    <td className="px-4 py-2 text-right text-xs text-nipost-green">{row.approved.toLocaleString('en-US')}</td>
                    <td className="px-4 py-2 text-right text-xs text-red-600">{row.rejected.toLocaleString('en-US')}</td>
                    <td className="px-4 py-2 text-right text-xs text-amber-700">{row.pending.toLocaleString('en-US')}</td>
                    <td className="px-4 py-2 text-right text-xs font-semibold">{row.approvalPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </ReportShell>
  );
}
